import logoUrl from '@field-support/design-tokens/assets/loynova-logo.png';
import { type FormEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/entities/session';
import { setLanguage, useErrorMessage } from '@/shared/i18n';
import { Button, Notice, TextInput } from '@/shared/ui';
import './login.css';

/** Sign-in (Tangram, D119): the Loynova brand panel beside the same form, same fields in the same order. */
export function LoginPage() {
  const { t, i18n } = useTranslation();
  const { login } = useAuth();
  const errorMessage = useErrorMessage();
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  // The error itself, translated at render: switching the language re-words it.
  const [error, setError] = useState<unknown>(null);
  const [submitting, setSubmitting] = useState(false);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await login(phone.trim(), password);
    } catch (err) {
      setError(err);
      setPassword('');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="login-page">
      <aside className="login-page__brand" data-theme="chrome">
        <span className="login-page__block" aria-hidden="true" />
        {/* The mark as supplied, on its white tile (it never sits directly on blue). */}
        <span className="login-page__mark">
          <img src={logoUrl} alt="Loynova" />
        </span>
        <div className="login-page__brand-text">
          <div className="login-page__brand-name">Field Support</div>
          <div className="login-page__brand-app">{t('common.appName')}</div>
        </div>
      </aside>
      <div className="login-page__card">
        <form className="login-page__form" onSubmit={submit} noValidate>
          <div className="login-page__header">
            <div className="login-page__brand-name-slot">
              <div className="login-page__wordmark">Field Support</div>
              <div className="login-page__app-name">{t('common.appName')}</div>
            </div>
            <Button variant="ghost" size="sm" onClick={() => setLanguage(i18n.language === 'ar' ? 'en' : 'ar')}>
              {t('common.language')}
            </Button>
          </div>
          <h1 className="login-page__title">{t('login.title')}</h1>
          <TextInput
            label={t('login.phone')}
            mono
            type="tel"
            inputMode="tel"
            autoComplete="username"
            placeholder={t('login.phoneHint')}
            value={phone}
            onChange={setPhone}
            size="lg"
            required
          />
          <TextInput
            label={t('login.password')}
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={setPassword}
            size="lg"
            required
          />
          {error != null && <Notice tone="fail">{errorMessage(error)}</Notice>}
          <Button type="submit" size="lg" fullWidth loading={submitting} disabled={!phone || !password}>
            {submitting ? t('login.signingIn') : t('login.submit')}
          </Button>
        </form>
      </div>
    </div>
  );
}
